import '@adyen/adyen-web/styles/adyen.css';

import type { CoreConfiguration } from '@adyen/adyen-web';
import { AdyenCheckout, Card, Dropin } from '@adyen/adyen-web';
import { useEffect, useRef } from 'react';

export type AdyenBillingAddress = {
  street?: string;
  houseNumberOrName?: string;
  city?: string;
  stateOrProvince?: string;
  postalCode?: string;
  country?: string;
};

export type AdyenPaymentCompletedResult = {
  resultCode: string;
  sessionResult?: string;
  cardHolderName?: string;
};

type AdyenDropInProps = {
  sessionId: string;
  sessionData: string;
  clientKey: string;
  environment: string;
  countryCode: string | null;
  onPaymentCompleted: (result: AdyenPaymentCompletedResult) => void;
  onError: (error: { message: string }) => void;
  /** Hide stored payment methods in the Drop-In (default: true — show them). */
  showStoredPaymentMethods?: boolean;
};

export const AdyenDropIn = ({
  sessionId,
  sessionData,
  clientKey,
  environment,
  countryCode,
  onPaymentCompleted,
  onError,
  showStoredPaymentMethods = true,
}: AdyenDropInProps) => {
  const dropinRef = useRef<HTMLDivElement>(null);
  const dropinInstanceRef = useRef<InstanceType<typeof Dropin> | null>(null);

  const onPaymentCompletedRef = useRef(onPaymentCompleted);
  onPaymentCompletedRef.current = onPaymentCompleted;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    let mounted = true;

    const initCheckout = async () => {
      if (!dropinRef.current) return;

      try {
        // Register components before creating the checkout (required in v6)
        AdyenCheckout.register(Dropin, Card);

        const checkout = await AdyenCheckout({
          environment: environment as 'test' | 'live',
          clientKey,
          countryCode: countryCode ?? undefined,
          session: { id: sessionId, sessionData },
          onPaymentCompleted: (
            result: {
              resultCode: string;
              sessionResult?: string;
            },
            _element: unknown
          ) => {
            if (mounted) {
              onPaymentCompletedRef.current({
                resultCode: result.resultCode,
                sessionResult: result.sessionResult,
              });
            }
          },
          onPaymentFailed: (result: { resultCode: string }) => {
            if (mounted) {
              onPaymentCompletedRef.current({
                resultCode: result.resultCode,
              });
            }
          },
          onError: (error: { message?: string }) => {
            if (mounted) {
              onErrorRef.current({
                message: error.message || 'Payment error',
              });
            }
          },
        } as CoreConfiguration);

        if (mounted && dropinRef.current) {
          const dropin = new Dropin(checkout, {
            showPaymentMethods: true,
            showStoredPaymentMethods,
            paymentMethodComponents: [Card],
            paymentMethodsConfiguration: {
              card: {},
            },
          });
          dropinInstanceRef.current = dropin;
          dropin.mount(dropinRef.current);
        }
      } catch (error) {
        console.error('Adyen Drop-in init error:', error);
        if (mounted) {
          onErrorRef.current({
            message:
              error instanceof Error
                ? error.message
                : 'Failed to initialize payment form',
          });
        }
      }
    };

    initCheckout();

    return () => {
      mounted = false;
      if (dropinInstanceRef.current) {
        try {
          dropinInstanceRef.current.unmount();
        } catch {
          // Cleanup errors are non-critical
        }
      }
    };
  }, [sessionId, sessionData, clientKey, environment, countryCode]);

  return <div ref={dropinRef} />;
};
